import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { LogIn, Mail, Lock, Eye, EyeOff, GraduationCap, Users, Building2, UserCheck, Clock } from 'lucide-react';
import Footer from '../components/layout/Footer';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(false);
  const { login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Check if redirected due to session expiry
    if (location.state?.sessionExpired) {
      setSessionExpiredMsg(true);
      // Clear the state after showing message
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSessionExpiredMsg(false);
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.message);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 relative" data-testid="login-page">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-5">
          <div className="mx-auto mb-3 flex items-center justify-center">
            <img 
              src="/cloud-campus-logo.png" 
              alt="Cloud Campus Logo" 
              className="h-14 w-auto object-contain"
            />
          </div>
          <p className="text-blue-600 text-sm font-medium">Smart ERP for Smart Institutions</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {sessionExpiredMsg && (
            <div className="bg-orange-50 border border-orange-200 text-orange-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="session-expired-message">
              <Clock className="h-4 w-4" />
              Your session has expired due to inactivity. Please log in again.
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm" data-testid="error-message">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email / Employee ID
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="admin@cloudcampus.com or ILOEEMP0001"
                required
                data-testid="email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="••••••••"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <a href="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
              Forgot Password?
            </a>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-300"
            data-testid="login-submit-button"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Portal Links */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500 mb-3">Other Portals</p>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/student/login"
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium text-sm"
              data-testid="student-login-link"
            >
              <GraduationCap size={16} />
              <span>Student</span>
            </Link>
            <Link
              to="/staff-portal"
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
              data-testid="staff-login-link"
            >
              <Users size={16} />
              <span>Staff</span>
            </Link>
            <Link
              to="/company-portal"
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors font-medium text-sm"
              data-testid="company-login-link"
            >
              <Building2 size={16} />
              <span>Company</span>
            </Link>
            <Link
              to="/alumni-portal"
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
              data-testid="alumni-login-link"
            >
              <UserCheck size={16} />
              <span>Alumni</span>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0">
        <Footer variant="minimal" />
      </div>
    </div>
  );
};

export default Login;
